import React, { useState } from 'react';
import './SignUp.css';

const SignUp: React.FC = () => {
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const checkPasswordRequirement = (requirement: string): boolean => {
    switch (requirement) {
      case 'length':
        return password.length >= 8;
      case 'capital':
        return /[A-Z]/.test(password);
      case 'lowercase':
        return /[a-z]/.test(password);
      case 'number':
        return /[0-9]/.test(password);
      case 'symbol':
        return /[!@#$%^&*(),.?":{}|<>]/.test(password);
      default:
        return false;
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setReferralCode(text);
    } catch (err) {
      console.error('Failed to read clipboard:', err);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreeToTerms) {
      alert('Please agree to the Terms of Use and Privacy Policy');
      return;
    }
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };

  return (
    <div className="signup-container">
      <header className="signup-header">
        <button className="back-button" aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="header-right">
          <span className="have-account-text">Have an account?</span>
          <button className="signin-link">Sign In</button>
        </div>
      </header>

      <div className="signup-content">
        <div className="signup-form-wrapper">
          <h1 className="signup-title">Sign Up</h1>
          <p className="signup-subtitle">Create your account to get started</p>

          <form onSubmit={handleSubmit} className="signup-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email/Phone number
              </label>
              <input
                id="email"
                type="text"
                className="form-input"
                placeholder="User@gmail.com / +234812345678"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="country" className="form-label">
                Country
              </label>
              <div className="select-wrapper">
                <select
                  id="country"
                  className="form-select"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                >
                  <option value="">Select Country</option>
                  <option value="US">United States</option>
                  <option value="UK">United Kingdom</option>
                  <option value="NG">Nigeria</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                </select>
                <svg className="select-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="password-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    {showPassword ? (
                      <path d="M14 8C14 8 11.5 12 8 12C4.5 12 2 8 2 8C2 8 4.5 4 8 4C11.5 4 14 8 14 8Z M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <>
                        <path d="M14 8C14 8 11.5 12 8 12C4.5 12 2 8 2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 8C2 8 4.5 4 8 4C11.5 4 14 8 14 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 2L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div className="password-requirements">
              <p className="requirements-title">Password must contain:</p>
              <div className="requirements-badges">
                <span className={`requirement-badge ${checkPasswordRequirement('length') ? 'met' : ''}`}>
                  Min. 8 characters
                </span>
                <span className={`requirement-badge ${checkPasswordRequirement('capital') ? 'met' : ''}`}>
                  Capital
                </span>
                <span className={`requirement-badge ${checkPasswordRequirement('lowercase') ? 'met' : ''}`}>
                  Lowercase
                </span>
                <span className={`requirement-badge ${checkPasswordRequirement('number') ? 'met' : ''}`}>
                  Number
                </span>
                <span className={`requirement-badge ${checkPasswordRequirement('symbol') ? 'met' : ''}`}>
                  Symbol
                </span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm password
              </label>
              <div className="password-input-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    {showConfirmPassword ? (
                      <path d="M14 8C14 8 11.5 12 8 12C4.5 12 2 8 2 8C2 8 4.5 4 8 4C11.5 4 14 8 14 8Z M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <>
                        <path d="M14 8C14 8 11.5 12 8 12C4.5 12 2 8 2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 8C2 8 4.5 4 8 4C11.5 4 14 8 14 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 2L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="referralCode" className="form-label">
                Referral Code <span className="optional-text">(optional)</span>
              </label>
              <div className="referral-input-wrapper">
                <input
                  id="referralCode"
                  type="text"
                  className="form-input referral-input"
                  placeholder="Enter referral code"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                />
                <button
                  type="button"
                  className="paste-button"
                  onClick={handlePaste}
                >
                  Paste
                </button>
              </div>
            </div>

            <div className="terms-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                />
                <span>
                  I agree to the{' '}
                  <button type="button" className="terms-link">Terms of Use</button>
                  {' '}and{' '}
                  <button type="button" className="terms-link">Privacy Policy</button>
                </span>
              </label>
            </div>

            <button type="submit" className="signup-button">
              Create Account
            </button>
          </form>
        </div>
      </div>

      {showSuccess && (
        <div className="success-notification">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M16.667 5L7.5 14.167L3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Account created successfully!</span>
        </div>
      )}
    </div>
  );
};

export default SignUp;